<template>
  <div class="height100 d-flex justify-center align-center text-center" style="font-size: 12px;">
    <v-progress-circular
      v-if="!errorLoading"
      color="primary"
      indeterminate
      :size="200"
    >手続きを進めています...</v-progress-circular>

    <v-progress-circular
      v-if="errorLoading"
      color="primary"
      indeterminate
      :size="200"
    >エラー処理を実行しています...</v-progress-circular>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import { Toast } from '@/service/SweetAlert';
import { ERROR_CODE_CARD_3DS_AUTH_FAILED, getApiErrorCode } from '@/api/ApiClient';
import { countCardRegisterFailure, resetCardRegisterFailure } from '@/service/CardRegisterLock';

import { familyModule } from '@/store/entityModules/family';
import { paymentModule } from '@/store/entityModules/payment';

/**
 * クレジットカード新規登録における3Dセキュア認証チャレンジ完了後の戻り先ページ。
 *
 * GMOのACS認証画面への遷移はフルページ遷移(location.href)のため、
 * このページに戻ってきた時点でVuexのstateは全てリセットされている。
 * そのため、PaymentRegister.vue が遷移前にsessionStorageへ退避しておいた
 * 以下の情報のみを使って処理する:
 *   accessPass, paymentCardTargetFamilyId,
 *   paymentCardTargetChildId, paymentCardTargetChildName
 *
 * ※ 以前このファイルにあった「支払方法(使用中カード)選択」用の3Dセキュア
 *   完了処理(targetChildId/selectedCardSeq/selectedCategoryを読む分岐)は、
 *   それらのsessionStorageキーをセットして遷移する処理が現行コードのどこにも
 *   存在せず、実質到達不能な死んだコードだったため削除した。
 *   もし将来その機能を実装する場合は、redirect前にPayment.vue側で
 *   同様のsessionStorage退避処理を追加した上で、このファイルにも
 *   対応する完了処理を復活させること。
 */
export default defineComponent({
  name: 'Secure',

  data: (): { errorLoading: boolean } => ({
    errorLoading: false,
  }),

  async mounted() {
    const accessID = this.$route.query.AccessID as string;
    const accessPass = sessionStorage.getItem('accessPass');

    if (!accessID || !accessPass) {
      this.errorLoading = true;
      Toast.fire({
        icon: 'error',
        title: '認証情報が見つかりませんでした',
      });
      this.$router.replace('/payment');
      return;
    }

    await this.executeCardSecureTran(accessID, accessPass);
  },

  methods: {
    async executeCardSecureTran(accessID: string, accessPass: string) {
      const childId = sessionStorage.getItem('paymentCardTargetChildId') ?? '';
      const childName = sessionStorage.getItem('paymentCardTargetChildName') ?? '';
      const familyIdStr = sessionStorage.getItem('paymentCardTargetFamilyId');
      const familyId = familyIdStr ? Number(familyIdStr) : null;

      const cleanUpStorage = () => {
        sessionStorage.removeItem('accessPass');
        sessionStorage.removeItem('paymentCardTargetFamilyId');
        sessionStorage.removeItem('paymentCardTargetChildId');
        sessionStorage.removeItem('paymentCardTargetChildName');
      };

      // 結果ダイアログはデータ再取得後に表示する (失敗時はnull以外)
      let failure: { title: string; text: string } | null = null;
      try {
        if (familyId == null) {
          throw new Error('familyId not found');
        }

        await paymentModule.postPaymentCardMethod({
          familyId,
          accessID,
          accessPass,
        });

        cleanUpStorage();
      } catch (error) {
        this.errorLoading = true;
        cleanUpStorage();
        console.error(error);
        failure = getApiErrorCode(error) === ERROR_CODE_CARD_3DS_AUTH_FAILED
          ? {
            title: '本人認証に失敗しました',
            text: 'カード会社の本人認証（3Dセキュア）が完了しなかったため、カードは登録されていません。',
          }
          : {
            title: 'カード登録でエラーが発生しました',
            text: 'お手数ですが、もう一度お試しください。',
          };
      }

      // GMOのACS認証はフルページ遷移(location.href)のため、このページに
      // 戻ってきた時点でVuexのstateは全てリセットされている。
      // Payment.vue は family / paymentModule.payments が既に読み込まれている
      // ことを前提にしており、自分では再取得しないため、遷移前にここで
      // 再取得しておく(失敗してもPayment.vue側で/payment/selectへ戻す
      // フォールバックがあるため、遷移自体は継続する)。
      if (familyId != null) {
        try {
          await familyModule.fetchFamilyById({ familyId });
          await paymentModule.fetchPaymentMethod({ familyId });
        } catch (e) {
          console.error(e);
        }
      }

      if (failure) {
        // 3Dセキュア認証後の失敗もカード登録の失敗としてカウントする（3回で30分ロック）
        await countCardRegisterFailure(failure.title, failure.text);
      } else {
        // 登録成功したらリトライカウントをクッキーから削除
        resetCardRegisterFailure();
        await Swal.fire({
          icon: 'success',
          title: 'カードの登録が完了しました',
          padding: '1rem 0',
        });
      }
      this.backToPayment(childId, childName);
    },

    backToPayment(childId: string, childName: string) {
      this.$router.replace({
        name: 'Payment',
        query: { childId, childName },
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.height100 {
  height: 100vh;
}
</style>
